"use client";
import * as React from 'react';
import { cn } from '@/lib/utils';
import { gestureSpeed, keyboardValue } from '@/lib/numeric';
import { useFreeValue, type FreeValueProps } from '@/hooks/use-freeui-value';

export interface FreeSliderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'onChange'>, FreeValueProps {
  thumb?: 'pointer' | 'round';
  levels?: { left: number; right: number };
  track?: string;
  showValue?: boolean;
  showTrack?: boolean;
  valueWidth?: React.CSSProperties['width'];
  accentColor?: string;
}

export function FreeNumberField(props: FreeValueProps & { valueWidth?: React.CSSProperties['width']; className?: string; accentColor?: string }) {
  const api = useFreeValue(props);
  const precision = props.precision ?? Math.min(6, Math.max(0, Math.ceil(-Math.log10(api.step))));
  const [text, setText] = React.useState(api.value.toFixed(precision));
  const [editing, setEditing] = React.useState(false);
  const cancelled = React.useRef(false);
  const typed = React.useRef(false);
  const drag = React.useRef<{ y: number; startY: number; moved: boolean } | null>(null);
  React.useEffect(() => { if (!editing) setText(api.value.toFixed(precision)); }, [api.value, precision, editing]);
  function commit() {
    if (cancelled.current) { cancelled.current = false; setEditing(false); return; }
    if (editing && typed.current && text.trim() && Number.isFinite(Number(text))) api.change(Number(text), true);
    typed.current = false;
    setEditing(false);
  }
  return <input className={cn('freeui-number-field', props.className)} aria-label={`${props.label || 'Value'} value`} inputMode="decimal"
    value={text} disabled={props.disabled} style={{ width: props.valueWidth, '--freeui-accent': props.accentColor } as React.CSSProperties}
    onChange={e => { typed.current = true; setEditing(true); setText(e.target.value); }} onFocus={() => { typed.current = false; setEditing(true); }} onBlur={commit}
    onKeyDown={e => {
      if (e.key === 'Escape') { cancelled.current = true; setText(api.value.toFixed(precision)); e.currentTarget.blur(); }
      else if (e.key === 'Enter') e.currentTarget.blur();
      else { const base = typed.current && text.trim() && Number.isFinite(Number(text)) ? Number(text) : api.current.current; const next = keyboardValue(e.key, base, api.min, api.max, api.step, gestureSpeed(e)); if (next !== undefined) { e.preventDefault(); api.change(next, true); typed.current = false; setText(api.current.current.toFixed(precision)); } }
    }}
    onPointerDown={e => {
      if (props.disabled || e.button !== 0 || editing) return;
      e.preventDefault();
      drag.current = { y: e.clientY, startY: e.clientY, moved: false };
      e.currentTarget.setPointerCapture(e.pointerId);
    }}
    onPointerMove={e => {
      const d = drag.current; if (!d) return;
      if (Math.abs(e.clientY - d.startY) > 3) d.moved = true;
      if (d.moved) { api.change(api.current.current + (d.y - e.clientY) * api.step * gestureSpeed(e)); d.y = e.clientY; }
    }}
    onPointerUp={e => { const d = drag.current; drag.current = null; if (!d) return; if (d.moved) api.commit(); else { e.currentTarget.focus(); e.currentTarget.select(); } }}
    onPointerCancel={() => { drag.current = null; }} />;
}

const tracks: Record<string, string> = {
  hue: 'linear-gradient(90deg,#ff5353,#f5ee5c,#61e47c,#53cafa,#866cf2,#ff5353)',
  saturation: 'linear-gradient(90deg,#777,var(--freeui-accent))',
  luminance: 'linear-gradient(90deg,#080808,#fff)'
};
export function FreeSlider({ thumb = 'pointer', levels, track = 'default', showValue = true, showTrack = true, valueWidth, accentColor, className, style, ...props }: FreeSliderProps) {
  const { value, defaultValue, onValueChange, onValueCommit, min, max, step, resetValue, precision, label, disabled, ...divProps } = props;
  const api = useFreeValue(props);
  const position = api.max === api.min ? 0 : (api.value - api.min) / (api.max - api.min);
  const drag = React.useRef<{ x: number } | null>(null);
  return <div data-slot="freeui-slider" data-thumb={thumb} className={cn('freeui-slider', className)} style={style} {...divProps}>
    {showTrack && <div className="freeui-slider-track-wrap">
      <div className="freeui-slider-track" role="slider" tabIndex={disabled ? -1 : 0} aria-label={label || 'Slider'} aria-valuemin={api.min} aria-valuemax={api.max} aria-valuenow={api.value} aria-disabled={disabled || undefined}
        style={{ '--freeui-slider-position': `${position * 100}%`, '--freeui-track': tracks[track] || (track === 'default' ? undefined : track) } as React.CSSProperties}
        onDoubleClick={() => api.reset()}
        onKeyDown={e => { if (disabled) return; const next = keyboardValue(e.key, api.value, api.min, api.max, api.step, gestureSpeed(e)); if (next !== undefined) { e.preventDefault(); api.change(next, true); } }}
        onPointerDown={e => { if (disabled || e.button !== 0) return; e.preventDefault(); e.currentTarget.focus(); e.currentTarget.setPointerCapture(e.pointerId); const r = e.currentTarget.getBoundingClientRect(); api.change(api.min + (e.clientX - r.left) / r.width * (api.max - api.min)); drag.current = { x: e.clientX }; }}
        onPointerMove={e => { if (!drag.current) return; const r = e.currentTarget.getBoundingClientRect(); api.change(api.current.current + (e.clientX - drag.current.x) / r.width * (api.max - api.min) * gestureSpeed(e)); drag.current.x = e.clientX; }}
        onPointerUp={() => { if (drag.current) api.commit(); drag.current = null; }} onPointerCancel={() => { drag.current = null; }}>
        <span className="freeui-slider-fill" /><span className="freeui-slider-thumb" />
      </div>
      {levels && <div className="freeui-stereo-meter">{(['left', 'right'] as const).map(channel => <div key={channel} role="meter" aria-label={`${label || 'Audio'} ${channel} level`} aria-valuemin={0} aria-valuemax={1} aria-valuenow={Math.min(1, Math.max(0, levels[channel]))}><span style={{ width: `${Math.min(1, Math.max(0, levels[channel])) * 100}%` }} /></div>)}</div>}
    </div>}
    {showValue && <FreeNumberField {...props} value={api.value} onValueChange={v => api.change(v)} onValueCommit={v => onValueCommit?.(v)} valueWidth={valueWidth} accentColor={accentColor} />}
  </div>;
}
